import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Button from '@/components/Shared/Button';
import Badge from '@/components/Shared/Badge';
import EmptyState from '@/components/Shared/EmptyState';
import { ChecklistTaskCard } from './ChecklistTaskCard';
import { toOpenDayGroups, useMyChecklistStore } from '@/store/useMyChecklistStore';
import { addIsoDays, isoDate } from '@/utils/dateUtils';

/**
 * The employee's own checklist surface: today's items, tick them off, move on — plus whatever
 * was left unconfirmed on earlier days, which stays completable indefinitely (ADR-0017).
 * Same shape as the Verwaltung protocol but scoped to the signed-in employee — hence no
 * employee filter, only an object filter for someone who works several sites in a day.
 */
export default function MyChecklistsPage() {
  const { t, i18n } = useTranslation('myChecklists');
  const { days, loading, error, fetchDay } = useMyChecklistStore();
  const openPast = useMyChecklistStore((s) => s.openPast);
  const openPastError = useMyChecklistStore((s) => s.openPastError);
  const fetchOpenPast = useMyChecklistStore((s) => s.fetchOpenPast);

  const today = useMemo(() => isoDate(new Date()), []);
  const [date, setDate] = useState(today);
  const [objectId, setObjectId] = useState('');
  // Off by default: the section then lists exactly what is still open, as before.
  const [showCompleted, setShowCompleted] = useState(false);

  useEffect(() => {
    fetchDay(date);
  }, [date, fetchDay]);

  useEffect(() => {
    fetchOpenPast(showCompleted);
  }, [fetchOpenPast, showCompleted]);

  const day = days[date];
  const allTasks = useMemo(() => day?.tasks ?? [], [day]);
  const isToday = date === today;
  // Only the future is read-only now; every past day can still be worked off.
  const isFuture = date > today;

  const pastGroups = useMemo(
    () =>
      toOpenDayGroups(openPast, today, showCompleted)
        .map((group) => ({
          ...group,
          tasks: objectId ? group.tasks.filter((task) => task.objectId === objectId) : group.tasks,
        }))
        .filter((group) => group.tasks.length > 0),
    [openPast, objectId, today, showCompleted],
  );

  const pastOpenCount = useMemo(
    () =>
      pastGroups.reduce(
        (sum, group) =>
          sum +
          group.tasks.reduce(
            (taskSum, task) => taskSum + (task.items ?? []).filter((i) => !i.isCompleted).length,
            0,
          ),
        0,
      ),
    [pastGroups],
  );

  // One entry per object present on the day — a plain <select> stays usable on a phone.
  const objectOptions = useMemo(() => {
    const seen = new Map<string, string>();
    const register = (task: { objectId?: string | null; objectName?: string | null }) => {
      if (task.objectId) seen.set(task.objectId, task.objectName ?? '–');
    };
    allTasks.forEach(register);
    toOpenDayGroups(openPast, today, showCompleted).forEach((group) =>
      group.tasks.forEach(register),
    );
    return Array.from(seen, ([id, label]) => ({ id, label }));
  }, [allTasks, openPast, today, showCompleted]);

  const tasks = useMemo(
    () => (objectId ? allTasks.filter((task) => task.objectId === objectId) : allTasks),
    [allTasks, objectId],
  );

  const openCount = useMemo(
    () => tasks.reduce((sum, task) => sum + (task.items ?? []).filter((i) => !i.isCompleted).length, 0),
    [tasks],
  );
  const doneCount = useMemo(
    () => tasks.reduce((sum, task) => sum + (task.items ?? []).filter((i) => i.isCompleted).length, 0),
    [tasks],
  );

  const formatDayLabel = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(
      i18n.language === 'de' ? 'de-AT' : i18n.language === 'sl' ? 'sl-SI' : 'en-US',
      { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' },
    );

  const displayDate = new Date(`${date}T00:00:00`).toLocaleDateString(
    i18n.language === 'de' ? 'de-AT' : i18n.language === 'sl' ? 'sl-SI' : 'en-US',
    { weekday: 'long', day: '2-digit', month: '2-digit' },
  );

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
          {t('title')}
        </h1>
        <p className="text-[13px] text-slate-500 mt-0.5">{t('subtitle')}</p>
        {/* Lives here, not in the section below: that one is hidden when nothing is open. */}
        <label className="mt-3 flex items-center gap-2 text-[12px] text-slate-600">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
          />
          {t('pastOpen.showCompleted')}
        </label>
      </div>

      {/* Left over from earlier days. No empty state: the section exists only for exceptions. */}
      {openPastError && (
        <EmptyState
          variant="error"
          title={t('errors.pastOpenFailed')}
          onRetry={() => fetchOpenPast(showCompleted)}
        />
      )}

      {!openPastError && pastGroups.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-white p-3 sm:p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="text-[18px] font-semibold tracking-tight text-slate-900">
                {showCompleted ? t('pastOpen.titleWithCompleted') : t('pastOpen.title')}
              </h2>
              <p className="text-[12px] text-slate-500 mt-0.5">
                {showCompleted ? t('pastOpen.subtitleWithCompleted') : t('pastOpen.subtitle')}
              </p>
            </div>
            <Badge variant={pastOpenCount > 0 ? 'warning' : 'default'}>
              {t('summary.open', { count: pastOpenCount })}
            </Badge>
          </div>

          {pastGroups.map((group) => (
            <div key={group.date} className="space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                {formatDayLabel(group.date)}
              </div>
              {group.tasks.map((task) => (
                <ChecklistTaskCard key={`${group.date}-${task.taskId}`} date={group.date} task={task} />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Day nav + object filter — one row on desktop, stacked on a phone. */}
      <div className="rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-2.5 space-y-2.5 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setDate(addIsoDays(date, -1))}
            aria-label={t('actions.previous')}
            className="p-1.5"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={2} />
          </Button>
          <input
            type="date"
            value={date}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            aria-label={t('filters.date')}
            className="flex-1 sm:flex-none px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setDate(addIsoDays(date, 1))}
            aria-label={t('actions.next')}
            className="p-1.5"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={2} />
          </Button>
          {!isToday && (
            <Button variant="secondary" size="sm" onClick={() => setDate(today)}>
              {t('actions.today')}
            </Button>
          )}
        </div>

        {objectOptions.length > 1 && (
          <select
            value={objectId}
            onChange={(e) => setObjectId(e.target.value)}
            aria-label={t('filters.object')}
            className="w-full sm:w-auto px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
          >
            <option value="">{t('filters.allObjects')}</option>
            {objectOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        )}

        <span className="hidden sm:inline text-[13px] text-slate-500 tabular-nums truncate">
          {displayDate}
        </span>
      </div>

      {loading && !day && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#00A871]" />
        </div>
      )}

      {error && !loading && (
        <EmptyState variant="error" title={t('errors.fetchFailed')} onRetry={() => fetchDay(date)} />
      )}

      {!error && day && (
        <>
          <div className="flex items-center gap-2">
            <Badge variant="success">{t('summary.done', { count: doneCount })}</Badge>
            <Badge variant={openCount > 0 ? 'warning' : 'default'}>
              {t('summary.open', { count: openCount })}
            </Badge>
            {isFuture && <Badge variant="default">{t('readOnly')}</Badge>}
          </div>

          {tasks.length === 0 ? (
            <EmptyState title={t('empty')} />
          ) : (
            <div className="space-y-3">
              {tasks.map((task) => (
                <ChecklistTaskCard key={task.taskId} date={date} task={task} readOnly={isFuture} />
              ))}
            </div>
          )}
        </>
      )}

    </div>
  );
}
